import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Target,
  UserPlus,
  BarChart3,
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  CalendarDays,
  type LucideIcon,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home / ia / recrutement —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-2xl font-bold text-[#30475e] mb-2 leading-tight';

const bodyClass = 'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Définition du catalogue d'outils —
interface Tool {
  name: string;
  tagline: string;
  description: string;
  mockup: string;
  mockupAlt: string;
  /** mockupFit = "contain" pour les SVG / logos, "cover" pour les screenshots */
  mockupFit: 'contain' | 'cover';
  url: string;
  cta: string;
  external: boolean;
  badge: { label: string; Icon: LucideIcon };
}

const tools: Tool[] = [
  {
    name: 'Mapping RH',
    tagline: 'Le score de compatibilité candidat ↔ équipe',
    description:
      "En 5 curseurs et une description de poste, obtenez un score de compatibilité, les zones de friction probables et des recommandations actionnables pour le manager. Conçu en interne chez recruteurs.io depuis 2017, ouvert à tous depuis 2025.",
    mockup:
      'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/MappingRH.svg',
    mockupAlt: 'Capture de Mapping RH — score de compatibilité candidat/équipe',
    mockupFit: 'contain',
    url: '/ressources/mapping-rh',
    cta: 'Lancer le test gratuit',
    external: false,
    badge: { label: 'Outil maison · GPT-4', Icon: Sparkles },
  },
  {
    name: 'PimpMyJob',
    tagline: "Reboostez vos offres d'emploi avec l'IA",
    description:
      "Importez votre offre d'emploi : l'IA la restructure, l'optimise pour les ATS, et ajoute ce qui fait vraiment la différence côté lecteur. En 30 secondes, votre annonce devient lisible, hiérarchisée et attractive.",
    mockup:
      'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/offreemploiia.svg',
    mockupAlt: 'Capture de PimpMyJob — IA qui reboost les offres d\'emploi',
    mockupFit: 'contain',
    url: 'https://pimpmyjob.fr',
    cta: 'Demander un accès',
    external: true,
    badge: { label: 'Outil maison · IA', Icon: Target },
  },
  {
    name: 'LoocatMe',
    tagline: "L'onboarding automatisé clé en main",
    description:
      "Plateforme No Code + contenus prêts à l'emploi. Pour les entreprises qui veulent industrialiser leur intégration sans construire un LMS de zéro — outils ET contenus livrés ensemble, déployés en quelques jours.",
    mockup:
      'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Onboarding%20marseille.png',
    mockupAlt: 'Capture de LoocatMe — plateforme d\'onboarding No Code',
    mockupFit: 'cover',
    url: 'https://loocatme.fr/',
    cta: 'Demander un accès',
    external: true,
    badge: { label: 'Famille LoocatMe', Icon: UserPlus },
  },
  {
    name: 'RESOO',
    tagline: 'Cockpit masse salariale pour réseaux retail',
    description:
      "Pour les chaînes de magasins ou d'agences multi-sites : RESOO transforme votre data salariale en verdicts de recrutement par site, en temps réel. Pas de dashboard décoratif — des décisions actionnables, mensuellement.",
    mockup:
      'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/resoo%20outil%20rh%20ia.png',
    mockupAlt: 'Capture de RESOO — cockpit masse salariale pour réseaux retail',
    mockupFit: 'cover',
    url: 'https://resoo.fr/',
    cta: 'Demander un accès',
    external: true,
    badge: { label: 'Famille LoocatMe', Icon: BarChart3 },
  },
  {
    name: 'Calendrier des échéances RH',
    tagline: 'Toutes vos obligations sociales, à leur date',
    description:
      "Index égalité, DSN, DOETH, taxe d'apprentissage, entretien professionnel, NAO, CSE, participation… Un calendrier interactif filtrable par mois et par effectif, avec la source officielle de chaque échéance. Pour les employeurs et DRH qui veulent sécuriser leur année sociale.",
    mockup:
      'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Cabinet-de-recrutement-marseille.webp',
    mockupAlt: 'Calendrier des échéances RH recruteurs.io',
    mockupFit: 'cover',
    url: '/ressources/outils/calendrier-rh',
    cta: 'Ouvrir le calendrier',
    external: false,
    badge: { label: 'Outil employeur · Gratuit', Icon: CalendarDays },
  },
  {
    name: 'Suivi de candidatures',
    tagline: 'Le tracker gratuit pour candidats organisés',
    description:
      "Kanban des statuts, relances automatiques, stats de conversion, export CSV. 100 % gratuit, sans inscription. Données stockées localement sur votre navigateur — aucune donnée envoyée à nos serveurs. Pour les candidats qui veulent reprendre le contrôle de leur recherche.",
    mockup:
      'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Cabinet-de-recrutement-marseille.webp',
    mockupAlt: 'Capture du tracker de candidatures recruteurs.io',
    mockupFit: 'cover',
    url: '/ressources/outils/suivi-candidature',
    cta: "Lancer l'outil",
    external: false,
    badge: { label: 'Outil candidat · Gratuit', Icon: Briefcase },
  },
];

export default function OutilsPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Nos outils RH augmentés par l'IA | recruteurs.io"
      description="Le catalogue d'outils RH et IA de recruteurs.io et de sa famille LoocatMe : Mapping RH, PimpMyJob, LoocatMe (onboarding), RESOO (pilotage masse salariale retail). Tous gratuits sur demande."
      keywords="outils RH IA, Mapping RH, PimpMyJob, LoocatMe onboarding, RESOO retail, suite outils recruteurs.io"
    >
      <WebPageSchema
        page={{
          title: "Nos outils RH augmentés par l'IA | recruteurs.io",
          description:
            "Catalogue d'outils RH gratuits de recruteurs.io et de la famille LoocatMe : Mapping RH, PimpMyJob, LoocatMe (onboarding), RESOO (pilotage retail).",
          url: 'https://recruteurs.io/ressources/outils',
          lastReviewed: '2026-05-11',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Ressources', url: 'https://recruteurs.io/ressources/articles' },
          { name: 'Outils', url: 'https://recruteurs.io/ressources/outils' },
        ]}
      />

      <HeroTemplateEnts
        page="outils"
        title="Nos outils RH augmentés par l'IA."
        eyebrow="Catalogue · recruteurs.io · Famille LoocatMe"
        description="Quatre outils gratuits ou accessibles sur demande, pour structurer vos recrutements, votre marque employeur, votre onboarding et votre pilotage RH. Conçus par recruteurs.io ou par notre écosystème — testés en interne avant d'être ouverts."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Conseil-IA-marseille.webp"
        breadcrumbs={[
          { name: 'Ressources', href: '/ressources/articles' },
          { name: 'Outils', href: '/ressources/outils' },
        ]}
        cta1={{
          label: 'Voir le catalogue',
          href: '#catalogue',
        }}
        cta2={{
          label: 'Demander un audit IA',
          onClick: () => navigate('/reservation'),
        }}
      />

      {/* CATALOGUE */}
      <SectionBackground
        page="outils"
        section="outils-catalogue"
        className="bg-white"
      >
        <Section>
          <div
            id="catalogue"
            className="max-w-4xl mx-auto text-center mb-14 scroll-mt-24"
          >
            <span className={eyebrowClass}>Le catalogue</span>
            <h2 className={h2Class}>
              Quatre outils,{' '}
              <span className={gradientText}>une seule philosophie.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              On ne vend pas du SaaS — on conçoit des outils qu'on utilise nous-mêmes
              en cabinet, puis on les ouvre à ceux qui veulent les essayer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {tools.map((tool, index) => {
              const BadgeIcon = tool.badge.Icon;
              const handleClick = () => {
                if (tool.external) {
                  window.open(tool.url, '_blank', 'noopener,noreferrer');
                } else {
                  navigate(tool.url);
                }
              };

              return (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="group flex flex-col rounded-[1.75rem] bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/30 hover:shadow-2xl shadow-sm overflow-hidden transition-all duration-300"
                >
                  {/* Mockup (haut de la card) */}
                  <div className="relative h-52 md:h-56 bg-gradient-to-br from-[#fff1f2] via-[#fef9c3]/40 to-[#e0f2ff] overflow-hidden flex items-center justify-center p-6">
                    <img
                      src={tool.mockup}
                      alt={tool.mockupAlt}
                      loading="lazy"
                      className={`${
                        tool.mockupFit === 'contain'
                          ? 'max-h-full max-w-full object-contain'
                          : 'w-full h-full object-cover rounded-xl'
                      } transition-transform duration-500 group-hover:scale-105`}
                    />
                    {/* Badge en overlay haut-droit */}
                    <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur-sm ring-1 ring-neutral-200 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#30475e]">
                      <BadgeIcon className="w-3 h-3 text-[#d03484]" />
                      {tool.badge.label}
                    </span>
                  </div>

                  {/* Contenu (corps de la card) */}
                  <div className="flex-1 flex flex-col p-7">
                    <h3 className={h3Class}>{tool.name}</h3>
                    <p className="text-sm font-semibold text-[#d03484] mb-3">
                      {tool.tagline}
                    </p>
                    <p className={`${bodyClass} flex-1 mb-6`}>{tool.description}</p>

                    {/* CTA (bas de la card) */}
                    <button
                      type="button"
                      onClick={handleClick}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all w-full md:w-auto md:self-start"
                    >
                      {tool.cta}
                      {tool.external ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowRight className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* CTA SECTION — Audit IA gratuit (restylée brand) */}
      <SectionBackground page="outils" section="outils-cta" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto rounded-[2rem] bg-gradient-to-br from-[#30475e] to-[#1f3245] p-10 md:p-12 text-white shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#fde7f2]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#fde7f2]">
                Tous ces outils sont inclus dans nos offres
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5 leading-[1.1]">
              Vous êtes déjà client recruteurs.io ?{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]">
                Tout est déjà à vous.
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              Pas d'option cachée, pas d'upsell surprise. Vous voulez voir comment
              ces outils — et l'IA en général — peuvent transformer vos process RH
              chronophages ? Profitez d'un audit gratuit, sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/offres')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-2 text-sm font-bold whitespace-nowrap text-[#30475e] hover:bg-[#fde7f2] hover:-translate-y-0.5 transition-all shadow-lg"
              >
                Voir nos offres
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-white/30 bg-white/10 backdrop-blur-sm px-5 py-2 text-sm font-semibold whitespace-nowrap text-white hover:bg-white/20 transition-all"
              >
                Demander mon audit IA gratuit
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
